/**
 * The theatre's right column: everything about the movement that is not its picture.
 *
 * Under the aliases line and „Twoje tagi" (`TheatreTags`, 10 October 2026), six blocks — Partie
 * mięśniowe, Sprzęt, Materiały, Metryki, Opis, Jak wykonać — separated by hairlines, in a column that scrolls inside itself. The order is not arbitrary: the two questions a
 * trainer asks before prescribing a movement are what it trains and whether the client can do it with
 * what they have, so the figures and the equipment stand together at the top (owner decision D6).
 * Materiały come straight after them (owner, 25 September 2026): the links are what the hero's source
 * tabs play, and at 1512 × 1000 this is the only place in the column they are on screen without a
 * scroll. The canon drew them fifth.
 *
 * ── Every block states its reserve, and all of them are known on the first frame ─────────────
 * Nothing in this column waits. The muscles, the equipment, the metrics, the prose, the steps and
 * the materials all arrive with the catalogue, and the ONE thing that lands late — which of the
 * three sentences „Materiały" prints — is reserved inside that block rather than here. The column
 * scrolls, so a long movement grows downward into its own scrollport and moves nothing beside it.
 *
 * ── The section headings are all `web:`, and that is the same rule the type labels follow ────
 * Four of the six exist verbatim in the mobile locale and two do not: the phone writes „Sprzęt do
 * wykorzystania" where the canon writes „Sprzęt", and „Mierzone" where the canon writes „Metryki".
 * Mixing namespaces inside one legend is what the list half avoided for its seven type labels, and
 * six headings down one column are one legend. So all six are `web:exerciseLibrary.theatre.sections.*`,
 * and the phone's own words are left to the phone.
 *
 * ── `Główne` / `Pomocnicze` ARE the phone's ────────────────────────────────────────────────
 * They are a pair, both exist upstream verbatim, and they are labels on a row rather than headings
 * in the legend above.
 *
 * ── The column SCROLLS, so it is a tab stop with a name ─────────────────────────────────────
 * At 1440 × 900 this column is 1072px of content in a 750px box — „Jak wykonać" is below the fold
 * from the first frame. A `div` with `overflow-y: auto` and no `tabindex` cannot be scrolled by a
 * keyboard at all, and the arrows that would have scrolled it were walking the card to the next
 * movement instead. `tabIndex={0}` + `role="region"` + a name is the same treatment `ExerciseTable`
 * already gives its own scroller, and it is what puts the rail's own keys back in its hands.
 */

import { useTranslation } from 'react-i18next';

import type { MeasurementFieldKey } from '~/shared/measurement';
import scrollbar from '~/shared/ui/scrollbar.module.css';

import type { ExerciseMaterialsState } from '../lib/useExerciseMaterials';
import type { ExerciseRow, MusclePill } from '../model/exerciseRow';

import { EquipmentTiles } from './EquipmentTiles';
import { MaterialsBlock } from './MaterialsBlock';
import { TheatreTags } from './TheatreTags';
import { MuscleFigures } from '~/entities/exercise-library';
import styles from './TheatreRail.module.css';

interface TheatreRailProps {
  readonly row: ExerciseRow;
  /** The movement's prose, already resolved into the reader's language. Empty where absent. */
  readonly description: string;
  /** `Jak wykonać`, in order. Empty where the movement carries none. */
  readonly instructions: readonly string[];
  /**
   * Which columns a set of this movement carries. NOT on `ExerciseRow`: the table draws none of
   * them, and a projection run 477 times a screen does not carry a field one card reads.
   */
  readonly measurementFields: readonly MeasurementFieldKey[];
  readonly materials: ExerciseMaterialsState;
  /** The movement's aliases in the content language (R20), and the trainer's tags on it (R21). */
  readonly exerciseId: number;
  readonly aliases: readonly string[];
  readonly tags: readonly string[];
}

export function TheatreRail({
  row,
  description,
  instructions,
  measurementFields,
  materials,
  exerciseId,
  aliases,
  tags,
}: TheatreRailProps) {
  const { t } = useTranslation();
  const primary = row.muscles.filter((pill) => !pill.secondary);
  const secondary = row.muscles.filter((pill) => pill.secondary);

  return (
    <div
      className={`${String(scrollbar.scrollbar)} ${String(styles.rail)}`}
      role="region"
      aria-label={t('web:exerciseLibrary.theatre.rail')}
      tabIndex={0}
    >
      {/* Above the muscles (canon `F1-szczegoly`): what else it is called, then the trainer's own
        * words for it. Ends on its own hairline. */}
      <TheatreTags exerciseId={exerciseId} aliases={aliases} tags={tags} />

      <section className={styles.block}>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.muscles')}</h3>
        {row.muscles.length === 0 ? (
          // A body with nothing lit reads as a render that failed, so it is not drawn at all — the
          // preview panel's own rule, on the same movement.
          <p className={styles.absence}>{t('web:exerciseLibrary.preview.noMuscles')}</p>
        ) : (
          <>
            <div className={styles.figures}>
              <MuscleFigures
                primary={primary.map((pill) => pill.group)}
                secondary={secondary.map((pill) => pill.group)}
                size="theatre"
              />
            </div>
            <MusclePills label={t('exerciseLibrary.detail.musclesPrimary')} pills={primary} />
            {secondary.length > 0 && (
              <MusclePills label={t('exerciseLibrary.detail.musclesSecondary')} pills={secondary} />
            )}
          </>
        )}
      </section>

      <hr className={styles.rule} />

      <section className={`${String(styles.block)} ${String(styles.equipment)}`}>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.equipment')}</h3>
        <EquipmentTiles pieces={row.equipment} />
      </section>

      <hr className={styles.rule} />

      <MaterialsBlock materials={materials} />

      <hr className={styles.rule} />

      <section className={styles.block}>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.metrics')}</h3>
        {measurementFields.length === 0 ? (
          // "It measures nothing" is a real prescription — a mobility drill the client simply does —
          // and it is a different statement from an empty row of chips.
          <p className={styles.chipAbsence}>{t('web:exerciseLibrary.theatre.noMetrics')}</p>
        ) : (
          // The phone's own nine words, `exerciseLibrary.field.*` — „KG", „Powt.", „Czas". They
          // already carry the unit where there is one, which is why nothing here appends a second.
          // Sorted by `measurementFieldsFromWire` into the dictionary's ONE canonical order, at the
          // entity's projection: mobile's own defect was the same exercise reading „REPS · KG · RPE"
          // when prescribed and „KG × REPS · RPE" when reviewed.
          <div className={styles.chips}>
            {measurementFields.map((field) => (
              <span key={field} className={styles.chip}>
                {t(`exerciseLibrary.field.${field}`)}
              </span>
            ))}
          </div>
        )}
      </section>

      <hr className={styles.rule} />

      <section className={styles.block}>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.description')}</h3>
        <p className={description === '' ? styles.proseAbsent : styles.prose}>
          {description === '' ? t('web:exerciseLibrary.theatre.noDescription') : description}
        </p>
      </section>

      <hr className={styles.rule} />

      <section className={styles.block}>
        <h3 className={styles.title}>{t('web:exerciseLibrary.theatre.sections.howTo')}</h3>
        {instructions.length === 0 ? (
          <p className={styles.absence}>{t('web:exerciseLibrary.theatre.noSteps')}</p>
        ) : (
          // A real sequence, so a numbered list rather than bullets. The numbers are the list's own
          // counter rather than text, so they are never out of step with the items.
          <ol className={styles.steps}>
            {/* The INDEX, because a movement whose steps repeat a sentence („Wróć do pozycji
              * wyjściowej.") gave two `<li>` one key. The list is rendered in order, never
              * reordered and never filtered, which is the case an index key is honest for. */}
            {instructions.map((step, index) => (
              <li key={index} className={styles.step}>
                {step}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/** One labelled row of muscle pills. Two callers with the same shape, so one component. */
function MusclePills({
  label,
  pills,
}: {
  readonly label: string;
  readonly pills: readonly MusclePill[];
}) {
  return (
    <div className={styles.muscleRow}>
      <span className={styles.muscleLabel}>{label}</span>
      <div className={styles.pills}>
        {pills.map((pill) => (
          <span
            key={pill.group}
            className={`${String(styles.pill)} ${pill.secondary ? String(styles.pillSecondary) : ''}`}
          >
            {pill.label}
          </span>
        ))}
      </div>
    </div>
  );
}
